import { defineComponent as _defineComponent } from 'vue'
import { createCommentVNode as _createCommentVNode, openBlock as _openBlock, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, createVNode as _createVNode } from "vue"

const _hoisted_1 = {
  "justify-content-center": "",
  wrap: "",
  class: "bg-gray"
}
const _hoisted_2 = {
  xs12: "",
  class: "bg-white main-layout"
}
const _hoisted_3 = { class: "ps-4 p-2" }
const _hoisted_4 = { class: "tracking-summary-row" }
const _hoisted_5 = { class: "tracking-column time-column" }
const _hoisted_6 = { class: "tracking-content" }
const _hoisted_7 = { class: "time-display" }
const _hoisted_8 = { class: "time-value" }
const _hoisted_9 = { class: "time-value" }
const _hoisted_10 = { class: "tracking-action" }
const _hoisted_11 = { class: "tracking-column lesson-column" }
const _hoisted_12 = { class: "tracking-content" }
const _hoisted_13 = { class: "time-display" }
const _hoisted_14 = { class: "time-value" }
const _hoisted_15 = { class: "d-flex p-2 ps-4" }
const _hoisted_16 = {
  class: "text-gray",
  style: { width: '85px' }
}
const _hoisted_17 = { class: "ms-4" }
const _hoisted_18 = ["onClick", "id"]
const _hoisted_19 = ["href"]
const _hoisted_20 = {
  key: 1,
  class: "d-flex justify-content-center"
}
const _hoisted_21 = {
  key: 2,
  class: "d-flex justify-content-center my-4"
}

import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { checkMobile } from '../../utils/utils'
import { headerState } from '../../stores/header'
import { footerState } from '../../stores/footer'
import api from '../../../api/v2/api'
import { Activity, Tracking } from '../../../api/v2/models/activity_data'
import LearningStatsData from '../../../api/v2/models/learning_stats_data'
import { format } from 'date-fns'
import UserTrackingChangeDialog from '../atoms_molecules/UserTrackingChangeDialog.vue'
import DashboardChart from '../organisms/DashboardChart.vue'
import { subMonths, eachDay } from 'date-fns'
import type { RouteLocationRaw } from 'vue-router'
import { userState } from '../../stores/user'

// Router

export default /*@__PURE__*/_defineComponent({
  __name: 'ActivityList',
  setup(__props) {

const route = useRoute()
const router = useRouter()

// Template refs
const dashboard = ref<InstanceType<typeof DashboardChart> | null>(null)

// Reactive state
const mobile = ref(checkMobile())
const loading = ref(false)
const forceReload = ref(true)

// ▼ ページ番号 (0スタート)
const page = ref(0)
// 最終ページかどうか（サーバーが返す）
const isEnd = ref(false)

const trackingUsers = ref<Tracking[]>([])
const activities = ref<Activity[]>([])
// 新方式（学習時間・完了数の数値のみ。個々のアクティビティ一覧は旧方式のフィードを引き続き使用）
const learningStatsData = ref<LearningStatsData | null>(null)
const useNewStats = computed(() => userState.user?.learningStatsReadSource === 'new')

const dateRange = ref({
  startDate: mobile.value ? new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) : subMonths(new Date(), 1),
  endDate: new Date()
})

// Computed properties
/**
 * 日付とアクティビティのまとめ
 */
const trackingUserSummaries = computed(() => {
  if (useNewStats.value) {
    const summaries = eachDay(
      new Date(dateRange.value.startDate),
      new Date(dateRange.value.endDate)
    ).map((currentDay) => {
      const dateStr = format(currentDay, 'YYYY-MM-DD')
      const day = learningStatsData.value?.days.find((d) => d.date === dateStr)
      const isLegacyEstimate = learningStatsData.value ? learningStatsData.value.isLegacyEstimate(dateStr) : true
      const legacyTracking = trackingUsers.value.find(
        (item) => dateFormat(item.trackingDate) === dateFormat(currentDay)
      )
      const dayActivities = activities.value.filter(
        (activity) => dateStr === format(new Date(activity.createdAt), 'YYYY-MM-DD')
      )
      const useLegacyTime = isLegacyEstimate || !!legacyTracking?.manuallyChanged
      const trackingUser = new Tracking({
        id: legacyTracking?.id ?? -1,
        trackingDate: currentDay,
        totalRecordedValue: useLegacyTime ? legacyTracking?.totalRecordedValue ?? 0 : day?.totalActiveSeconds ?? 0,
        totalExamValue: 0,
        manuallyChanged: legacyTracking?.manuallyChanged ?? false
      })
      return {
        trackingUser,
        activities: dayActivities,
        completedCount: isLegacyEstimate ? dayActivities.length : day?.completedLessonCount ?? 0,
        isLegacyEstimate
      }
    })
    return summaries.reverse()
  }

  const summaries = eachDay(
    new Date(dateRange.value.startDate),
    new Date(dateRange.value.endDate)
  ).map((currentDay) => {
    const trackingUser = trackingUsers.value.find(
      (item) => dateFormat(item.trackingDate) === dateFormat(currentDay)
    )

    if (trackingUser) {
      const dayActivities = activities.value.filter(
        (activity) =>
          dateFormat(trackingUser.trackingDate) ===
          dateFormat(activity.createdAt)
      )
      return { trackingUser, activities: dayActivities, completedCount: dayActivities.length, isLegacyEstimate: false }
    }
    // 該当する trackingUser が無い場合は空データ
    return { trackingUser: Tracking.createEmpty(currentDay), activities: [], completedCount: 0, isLegacyEstimate: false }
  })
  return summaries.reverse()
})

/**
 * ▼ 簡易的に「全ページ数」を推定する
 */
const totalPages = computed(() => {
  // 0スタートの page に合わせて算出
  if (isEnd.value) {
    // もう次は無い => 今のページが最終 => page+1
    return page.value + 1
  } else {
    // 次のページがあるかも => page+2
    return page.value + 2
  }
})

// Methods
/**
 * ダッシュボードから日付範囲が変わったときに呼ばれる
 */
const updateRangeDate = (newDateRange: { startDate: Date; endDate: Date }) => {
  dateRange.value = newDateRange
  // ページをリセット
  page.value = 0
  updateDateRange(page.value)
}

/**
 * Vuetifyのページネーションでページが切り替わったとき
 * newPage は 1スタートなので、実際の page へは (newPage - 1) をセット
 */
const onPageChange = (newPage: number) => {
  const actualPage = newPage - 1
  // すでに読み込み中でなければ
  if (!loading.value) {
    updateDateRange(actualPage)
  }
}

/**
 * APIから指定ページのデータを取得
 */
const updateDateRange = async (targetPage: number) => {
  if (loading.value) return
  loading.value = true

  try {
    if (!userState.user) await userState.getUser()
    const response = await api.activity.get(targetPage, dateRange.value)
    trackingUsers.value = response.trackingUsers
    activities.value = response.activities

    if (useNewStats.value) {
      learningStatsData.value = await api.learningStats.dailySeries(dateRange.value)
    }

    page.value = targetPage
    isEnd.value = response.isEnd
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}

/**
 * 学習時間を手動で更新
 */
const updateTracking = async (tracking: Tracking, totalRecordedValue: number) => {
  await api.tracking.updateTracking(
    tracking.id,
    totalRecordedValue,
    format(tracking.trackingDate, 'YYYY-MM-DD')
  )

  if (tracking.id === -1) {
    // 新規追加
    tracking.totalRecordedValue = totalRecordedValue
    tracking.manuallyChanged = true
    trackingUsers.value.push(tracking)
  } else {
    // 既存レコード更新
    const found = trackingUsers.value.find((item) => item.id === tracking.id)
    if (found) {
      found.totalRecordedValue = totalRecordedValue
      found.manuallyChanged = true
    }
  }

  // ダッシュボードの更新
  if (dashboard.value) {
    ;(dashboard.value as any).updateDateRange()
  }
}

/**
 * ▼ 以下、表示用ユーティリティ
 */
// "YYYY年MM月DD日" のようなフォーマット
const dateFormat = (date: Date) => {
  // 例: 「2025年02月16日」
  return format(date, 'YYYY年MM月DD日')
}

// "HH時mm分" のようなフォーマット
const timeFormat = (date: Date) => {
  return format(date, 'HH時mm分')
}

// 学習時間(分)を「時間:分」に変換
const timeValueToHour = (trackingUser: Tracking) => {
  return Math.floor(Math.round(trackingUser.totalRecordedValue / 60) / 60)
}

const timeValueToMinutes = (trackingUser: Tracking) => {
  return (
    Math.round(trackingUser.totalRecordedValue / 60) -
    timeValueToHour(trackingUser) * 60
  )
}

const saveNavigationPath = (link: any, id: string) => {
  sessionStorage.setItem('previousPage', route.path + '?id=activity-link-' + id)
  router.push(link)
}

const scrollToLink = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const id = urlParams.get('id')
  if (id) {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }
}

// Lifecycle
onMounted(async () => {
  headerState.setTitle('アクティビティ')
  footerState.setFooterDefault()
  await updateDateRange(page.value)
  scrollToLink()
})

return (_ctx: any,_cache: any) => {
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_pagination = _resolveComponent("v-pagination")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createCommentVNode(" ダッシュボードチャート部分 "),
      (forceReload.value)
        ? (_openBlock(), _createBlock(DashboardChart, {
            key: 0,
            ref_key: "dashboard",
            ref: dashboard,
            onRangeDate: updateRangeDate
          }, null, 512 /* NEED_PATCH */))
        : _createCommentVNode("v-if", true),
      _createCommentVNode(" 日付ごとのアクティビティ表示 "),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(trackingUserSummaries.value, (trackingUserSummary, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: index,
          class: "mb-2"
        }, [
          _createElementVNode("div", _hoisted_3, [
            _createTextVNode(_toDisplayString(dateFormat(trackingUserSummary.trackingUser.trackingDate)) + " ", 1 /* TEXT */),
            (trackingUserSummary.isLegacyEstimate)
              ? (_openBlock(), _createBlock(_component_v_chip, {
                  key: 0,
                  size: "x-small",
                  color: "grey-lighten-1",
                  class: "ms-1",
                  title: "この日付は新しい計測方式の開始日より前のため、旧方式による推定値を表示しています"
                }, {
                  default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
                    _createTextVNode(" 旧方式による推定値 ", -1 /* CACHED */)
                  ]))]),
                  _: 1 /* STABLE */
                }))
              : _createCommentVNode("v-if", true)
          ]),
          _createElementVNode("div", _hoisted_4, [
            _createCommentVNode(" 学習時間 Column "),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("span", _hoisted_8, _toDisplayString(timeValueToHour(trackingUserSummary.trackingUser)), 1 /* TEXT */),
                  _cache[1] || (_cache[1] = _createElementVNode("span", { class: "time-unit" }, "時間", -1 /* CACHED */)),
                  _createElementVNode("span", _hoisted_9, _toDisplayString(timeValueToMinutes(trackingUserSummary.trackingUser)), 1 /* TEXT */),
                  _cache[2] || (_cache[2] = _createElementVNode("span", { class: "time-unit" }, "分", -1 /* CACHED */))
                ]),
                _cache[3] || (_cache[3] = _createElementVNode("div", { class: "tracking-label" }, "学習時間", -1 /* CACHED */))
              ]),
              _createElementVNode("div", _hoisted_10, [
                _createCommentVNode(" 新方式でも手動変更は旧方式の記録に保存し、その日は手入力の値を表示する "),
                _createVNode(UserTrackingChangeDialog, {
                  tracking: trackingUserSummary.trackingUser,
                  update: updateTracking
                }, null, 8 /* PROPS */, ["tracking"])
              ])
            ]),
            _createCommentVNode(" 学習完了数 Column "),
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("div", _hoisted_12, [
                _createElementVNode("div", _hoisted_13, [
                  _createElementVNode("span", _hoisted_14, _toDisplayString(trackingUserSummary.completedCount), 1 /* TEXT */),
                  _cache[4] || (_cache[4] = _createElementVNode("span", { class: "time-unit" }, "レッスン", -1 /* CACHED */))
                ]),
                _cache[5] || (_cache[5] = _createElementVNode("div", { class: "tracking-label" }, "学習完了数", -1 /* CACHED */))
              ])
            ])
          ]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(trackingUserSummary.activities, (activity, index) => {
            return (_openBlock(), _createElementBlock("div", { key: index }, [
              _createElementVNode("div", _hoisted_15, [
                _createElementVNode("div", _hoisted_16, _toDisplayString(timeFormat(activity.createdAt)), 1 /* TEXT */),
                _createElementVNode("div", _hoisted_17, [
                  (activity.link)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        onClick: ($event: any) => (saveNavigationPath(activity.link, activity.id.toString())),
                        class: "link-text",
                        id: 'activity-link-' + activity.id
                      }, _toDisplayString(activity.title), 9 /* TEXT, PROPS */, _hoisted_18))
                    : (activity.externalLink)
                      ? (_openBlock(), _createElementBlock("a", {
                          key: 1,
                          href: activity.externalLink,
                          target: "_blank",
                          rel: "noopener noreferrer"
                        }, _toDisplayString(activity.title), 9 /* TEXT, PROPS */, _hoisted_19))
                      : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                          _createTextVNode(_toDisplayString(activity.title) + " ", 1 /* TEXT */),
                          _cache[6] || (_cache[6] = _createElementVNode("span", { class: "link-not-found" }, "リンクが無効になりました", -1 /* CACHED */))
                        ], 64 /* STABLE_FRAGMENT */))
                ])
              ]),
              _createVNode(_component_v_divider)
            ]))
          }), 128 /* KEYED_FRAGMENT */))
        ]))
      }), 128 /* KEYED_FRAGMENT */)),
      _createCommentVNode(" ローディングスピナー "),
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
            _createVNode(_component_v_progress_circular, {
              size: 50,
              color: "primary",
              indeterminate: ""
            })
          ]))
        : _createCommentVNode("v-if", true),
      _createCommentVNode(" ▼ Vuetify2 のページネーション "),
      (!loading.value && totalPages.value > 1)
        ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
            _createCommentVNode(" v-pagination は1スタートなので、:value=\"page + 1\" をバインドする "),
            _createVNode(_component_v_pagination, {
              value: page.value + 1,
              length: totalPages.value,
              onInput: onPageChange,
              "total-visible": 5
            }, null, 8 /* PROPS */, ["value", "length"])
          ]))
        : _createCommentVNode("v-if", true)
    ])
  ]))
}
}

})